/**
 * BinderPanel — the audit's folder-organized record room ("the binder").
 *
 * Folders: Disclosed issues (the existing PAIS store — one source of truth),
 * ONE Universes folder for every audit type, Data integrity, the protocol's
 * review areas, and Notices. Every folder accepts issues, notes, and file
 * attachments; the Universes folder also scrubs universe files.
 *
 * NO-PHI, three layers, all server-enforced:
 *   - typed/pasted text with a detectable identifier is refused at save;
 *   - attachments ride the evidence pipeline: sealed bytes + automatic
 *     quarantine (bytes and text removed, Compliance alerted) on detection;
 *   - universe files are never stored here at all — only masked results.
 * The amber notice in every folder tells the user before they upload.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';

const API = process.env.REACT_APP_API_URL;

const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);

export function binderFolders(audit) {
  const spec = audit?.protocol_spec || null;
  const folders = [{ key: 'disclosed_issues', label: 'Disclosed issues', hint: 'What the auditee disclosed before and during the audit — the pre-audit issue summary.' }];
  // ONE Universes folder for every audit type — ODAG, CDAG, SNPCC, focused,
  // taught protocols alike. Each scrubbed file is its own entry inside.
  folders.push({ key: 'universe:general', label: 'Universes', universe: true, hint: 'Every scrubbed universe for this audit — findings only (formatting, untimely), never member data.' });
  folders.push({ key: 'data_integrity', label: 'Data integrity', hint: 'The universe data checks, powered by the Scrubber.' });
  const areas = Array.isArray(spec?.sample_plan) && spec.sample_plan.length
    ? spec.sample_plan.filter(s => !/universe integrity/i.test(s.name || '')).map(s => ({ key: `area:${slug(s.name)}`, label: s.name, hint: s.note || '' }))
    : [{ key: 'area:evidence', label: 'Evidence & findings', hint: 'What the review found, saved as issues and notes.' }];
  folders.push(...areas);
  folders.push({ key: 'notices', label: 'Notices', hint: 'Draft and final notice reviews — content, timeliness, delivery.' });
  return folders;
}

const STATUS = {
  open: { label: 'Open', cls: 'bg-amber-50 text-amber-700 border-amber-200', next: 'in_progress' },
  in_progress: { label: 'Being fixed', cls: 'bg-blue-50 text-blue-700 border-blue-200', next: 'resolved' },
  resolved: { label: 'Fixed', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', next: 'open' },
};

function StatusChip({ status, onCycle, busy }) {
  const cfg = STATUS[status];
  if (!cfg) return null;
  return (
    <button type="button" onClick={onCycle} disabled={busy} title="Click to change status"
      className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 ${cfg.cls} disabled:opacity-50`}>
      {cfg.label}
    </button>
  );
}

// The no-PHI notice every folder (and every upload surface) carries.
export function PhiUploadNotice() {
  return (
    <div className="flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
      <span aria-hidden="true" className="mt-px">🛡️</span>
      <span>
        <b>No PHI here.</b> Do not upload or type member names, IDs, birth dates, or any protected health information.
        Detected PHI is removed automatically and Compliance is alerted. <b>If in doubt, ask Compliance first.</b>
      </span>
    </div>
  );
}

const fmtSize = (b) => {
  const n = Number(b) || 0;
  if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
};

export function BinderPanel({ audit, auditId }) {
  const folders = useMemo(() => binderFolders(audit), [audit]);
  const [sel, setSel] = useState(folders[0]?.key || 'disclosed_issues');
  const [entries, setEntries] = useState([]);
  const [disclosed, setDisclosed] = useState([]);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(true);
  // Add form
  const [addKind, setAddKind] = useState('issue');
  const [addTitle, setAddTitle] = useState('');
  const [addDetail, setAddDetail] = useState('');
  // Disclosed-issue form (existing PAIS store)
  const [dDesc, setDDesc] = useState('');
  const [dDate, setDDate] = useState('');
  const [dCorr, setDCorr] = useState('');
  // Uploads: one input for universe scrubs, one for folder attachments.
  const scrubRef = useRef(null);
  const attachRef = useRef(null);
  const pendingFolder = useRef(null);

  const loadBinder = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/audit/audits/${auditId}/binder`, { credentials: 'include' }).then(r => r.json());
      if (r.success) setEntries(r.entries || []);
    } catch {}
  }, [auditId]);
  const loadDisclosed = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/audit/audits/${auditId}/disclosed-issues`, { credentials: 'include' }).then(r => r.json());
      if (r.success) setDisclosed(r.issues || []);
    } catch {}
  }, [auditId]);
  useEffect(() => { loadBinder(); loadDisclosed(); }, [loadBinder, loadDisclosed]);

  // The Universes folder owns every 'universe:*' entry — including any saved
  // under an older per-table key — so nothing ever disappears from view.
  const inFolder = (e, f) => (f.universe ? String(e.folder_key).startsWith('universe:') : e.folder_key === f.key);
  const countFor = (f) => {
    if (f.key === 'disclosed_issues') return disclosed.length;
    if (f.key === 'data_integrity') return (audit?.data_integrity ? 1 : 0) + entries.filter(e => inFolder(e, f)).length;
    return entries.filter(e => inFolder(e, f)).length;
  };
  const openCountFor = (f) => entries.filter(e => inFolder(e, f) && e.status === 'open').length;

  const addEntry = async (folderKey) => {
    if (!addTitle.trim()) return;
    setBusy('add'); setErr('');
    try {
      const d = await fetch(`${API}/api/audit/audits/${auditId}/binder`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folder_key: folderKey, kind: addKind, title: addTitle.trim(), detail: addDetail.trim() || null }),
      }).then(r => r.json());
      if (!d.success) throw new Error(d.message || 'Could not save the entry.');
      setAddTitle(''); setAddDetail('');
      await loadBinder();
    } catch (e) { setErr(e.message); }
    setBusy('');
  };

  const cycleStatus = async (entry) => {
    const next = STATUS[entry.status]?.next; if (!next) return;
    setBusy(entry.id); setErr('');
    try {
      const d = await fetch(`${API}/api/audit/audits/${auditId}/binder/${entry.id}`, {
        method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: next }),
      }).then(r => r.json());
      if (!d.success) throw new Error(d.message || 'Could not update.');
      await loadBinder();
    } catch (e) { setErr(e.message); }
    setBusy('');
  };

  const addDisclosed = async () => {
    if (!dDesc.trim()) return;
    setBusy('disclosed'); setErr('');
    try {
      const d = await fetch(`${API}/api/audit/audits/${auditId}/disclosed-issues`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: dDesc.trim(), disclosed_date: dDate || null, correction_status: dCorr.trim() || null }),
      }).then(r => r.json());
      if (!d.success) throw new Error(d.message || 'Could not record the issue.');
      setDDesc(''); setDDate(''); setDCorr('');
      await loadDisclosed();
    } catch (e) { setErr(e.message); }
    setBusy('');
  };
  const removeDisclosed = async (id) => {
    setBusy('disclosed'); setErr('');
    try {
      const d = await fetch(`${API}/api/audit/audits/${auditId}/disclosed-issues/${id}`, { method: 'DELETE', credentials: 'include' }).then(r => r.json());
      if (!d.success) throw new Error(d.message || 'Could not remove the issue.');
      await loadDisclosed();
    } catch (e) { setErr(e.message); }
    setBusy('');
  };

  const pickScrub = (folderKey) => { pendingFolder.current = folderKey; scrubRef.current?.click(); };
  const scrubUniverse = async (file) => {
    const folderKey = pendingFolder.current; if (!folderKey || !file) return;
    setBusy('scrub'); setErr('');
    try {
      const fd = new FormData();
      fd.append('universe', file);
      fd.append('folder_key', folderKey);
      const d = await fetch(`${API}/api/audit/audits/${auditId}/binder/scrub`, { method: 'POST', credentials: 'include', body: fd }).then(r => r.json());
      if (!d.success) throw new Error(d.message || 'The scrub failed.');
      await loadBinder();
    } catch (e) { setErr(e.message); }
    setBusy('');
  };

  const pickAttach = (folderKey) => { pendingFolder.current = folderKey; attachRef.current?.click(); };
  const attachFile = async (file) => {
    const folderKey = pendingFolder.current; if (!folderKey || !file) return;
    setBusy('attach'); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder_key', folderKey);
      const d = await fetch(`${API}/api/audit/audits/${auditId}/binder/attach`, { method: 'POST', credentials: 'include', body: fd }).then(r => r.json());
      if (!d.success) throw new Error(d.message || 'The upload failed.');
      await loadBinder();
    } catch (e) { setErr(e.message); }
    setBusy('');
  };

  const downloadAttachment = async (entry) => {
    const docId = entry.data?.document_id; if (!docId) return;
    setErr('');
    try {
      const r = await fetch(`${API}/api/audit/audits/${auditId}/binder/attachment/${docId}/download`, { credentials: 'include' });
      if (!r.ok) {
        const d = await r.json().catch(() => null);
        throw new Error(d?.message || 'Download failed.');
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = entry.data?.file_name || 'attachment';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(e.message); }
  };

  // One renderer for every entry kind — issue/note, universe result, attachment.
  const renderEntry = (e) => {
    if (e.kind === 'universe_result') {
      const d = e.data || {};
      const c = d.counts || {};
      return (
        <div key={e.id} className="border border-slate-100 rounded-lg px-3 py-2 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span aria-hidden="true" className="text-[13px]">🧹</span>
            <span className="text-[12px] font-semibold text-slate-700 flex-1 min-w-0 truncate">{d.file_name || e.title}</span>
            {d.metrics?.universe_type && <span className="text-[10px] font-mono bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{d.metrics.universe_type}</span>}
            <StatusChip status={e.status} onCycle={() => cycleStatus(e)} busy={busy === e.id} />
          </div>
          <div className="flex flex-wrap gap-1.5 text-[10px]">
            <span className={`rounded-full px-2 py-0.5 font-semibold ${c.formatting ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>{c.formatting || 0} formatting issue{c.formatting === 1 ? '' : 's'}</span>
            <span className={`rounded-full px-2 py-0.5 font-semibold ${c.untimely ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>{c.untimely || 0} untimely</span>
            {c.critical > 0 && <span className="rounded-full px-2 py-0.5 font-semibold bg-red-100 text-red-700">{c.critical} critical</span>}
            {e.data?.metrics?.total_rows != null && <span className="rounded-full px-2 py-0.5 bg-slate-100 text-slate-600">{e.data.metrics.total_rows} rows{e.data.metrics.error_rate ? ` · ${e.data.metrics.error_rate}` : ''}</span>}
            <span className="text-slate-400 px-1 py-0.5">{new Date(e.created_at).toLocaleDateString()}</span>
          </div>
          {e.data?.summary && <div className="text-[11px] text-slate-500">{e.data.summary}</div>}
          {Array.isArray(e.data?.recommendations) && e.data.recommendations.length > 0 && (
            <details className="text-[11px] text-slate-500">
              <summary className="cursor-pointer font-semibold text-slate-400">What to fix ({e.data.recommendations.length})</summary>
              <ul className="list-disc list-inside mt-1 space-y-0.5">{e.data.recommendations.slice(0, 10).map((r, i) => <li key={i}>{r}</li>)}</ul>
            </details>
          )}
        </div>
      );
    }
    if (e.kind === 'attachment') {
      const d = e.data || {};
      return (
        <div key={e.id} className="border border-slate-100 rounded-lg px-3 py-2 flex items-center gap-2.5">
          <span aria-hidden="true" className="text-[15px]">📎</span>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-semibold text-slate-700 truncate">{d.source === 'notice_send' ? e.title : (d.file_name || e.title)}</div>
            <div className="text-[10px] text-slate-400">{fmtSize(d.size)} · {new Date(e.created_at).toLocaleDateString()}{e.author_name ? ` · ${e.author_name}` : ''}{d.source === 'notice_send' ? ` · ${d.sent_via === 'manual' ? 'recorded as sent' : 'sent'}` : ' · scanned for PHI on upload'}</div>
            {Array.isArray(d.recipients) && d.recipients.length > 0 && (
              <div className="text-[10px] text-slate-500 truncate" title={d.recipients.join(', ')}>To: {d.recipients.join(', ')}</div>
            )}
          </div>
          <button type="button" onClick={() => downloadAttachment(e)}
            className="text-[11px] font-semibold text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-2.5 py-1">
            ↓ Download
          </button>
        </div>
      );
    }
    return (
      <div key={e.id} className="border border-slate-100 rounded-lg px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-semibold text-slate-700 flex-1 min-w-0">{e.title}</span>
          {e.kind === 'issue' && <StatusChip status={e.status} onCycle={() => cycleStatus(e)} busy={busy === e.id} />}
        </div>
        {e.detail && <div className="text-[11px] text-slate-500 mt-1 whitespace-pre-wrap">{e.detail}</div>}
        <div className="text-[10px] text-slate-400 mt-1">{e.author_name ? `${e.author_name} · ` : ''}{new Date(e.created_at).toLocaleDateString()}</div>
      </div>
    );
  };

  const folder = folders.find(f => f.key === sel) || folders[0];
  const folderEntries = entries.filter(e => inFolder(e, folder));
  const di = audit?.data_integrity || null;

  const AttachButton = ({ folderKey }) => (
    <button type="button" onClick={() => pickAttach(folderKey)} disabled={busy === 'attach'}
      className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50">
      {busy === 'attach' ? 'Uploading…' : '📎 Attach file'}
    </button>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <input ref={scrubRef} type="file" accept=".xlsx,.xls,.csv,.txt" className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) scrubUniverse(f); e.target.value = ''; }} />
      <input ref={attachRef} type="file" accept=".pdf,.doc,.docx,.txt,.csv,.md" className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) attachFile(f); e.target.value = ''; }} />
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between text-left">
        <div>
          <div className="text-sm font-semibold text-slate-800">Audit binder</div>
          <div className="text-[11px] text-slate-400">Everything saved on this audit, filed by folder — issues, universe results, data integrity, review areas, notices, attachments. Findings only, never member data.</div>
        </div>
        <span className="text-slate-300 text-sm">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="grid md:grid-cols-[240px,minmax(0,1fr)] gap-3 items-start">
          {/* Folder rail */}
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            {folders.map(f => {
              const n = countFor(f); const openN = openCountFor(f);
              return (
                <button key={f.key} type="button" onClick={() => setSel(f.key)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left border-b border-slate-100 last:border-b-0 ${sel === f.key ? 'bg-orange-50' : 'hover:bg-slate-50'}`}>
                  <span className="text-[13px]" aria-hidden="true">📁</span>
                  <span className={`flex-1 min-w-0 truncate text-[12px] ${sel === f.key ? 'font-semibold text-slate-800' : 'text-slate-600'}`}>
                    {f.label}
                  </span>
                  {openN > 0 && <span className="text-[10px] font-semibold bg-amber-100 text-amber-700 rounded-full px-1.5">{openN}</span>}
                  {n > 0 && <span className="text-[10px] text-slate-400">{n}</span>}
                </button>
              );
            })}
          </div>

          {/* Folder pane */}
          <div className="space-y-3 min-w-0">
            <div>
              <div className="text-[13px] font-semibold text-slate-800">{folder.label}</div>
              {folder.hint && <div className="text-[11px] text-slate-400">{folder.hint}</div>}
            </div>
            <PhiUploadNotice />
            {err && <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{err}</div>}

            {/* Disclosed issues — the existing PAIS store, one source of truth */}
            {folder.key === 'disclosed_issues' && (
              <>
                {disclosed.length === 0 && folderEntries.length === 0 && <div className="text-[12px] text-slate-400">Nothing disclosed yet. Issues the auditee reported before or during the audit get filed here.</div>}
                {disclosed.map(it => (
                  <div key={it.id} className="border border-slate-100 rounded-lg px-3 py-2">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0 text-[12px] text-slate-700">{it.description}</div>
                      <button type="button" onClick={() => removeDisclosed(it.id)} disabled={busy === 'disclosed'} className="text-slate-300 hover:text-red-600 flex-shrink-0" aria-label="Remove disclosed issue">×</button>
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-slate-400 mt-1">
                      {it.disclosed_date && <span>Disclosed {String(it.disclosed_date).slice(0, 10)}</span>}
                      {it.correction_status && <span>Correction: {it.correction_status}</span>}
                    </div>
                  </div>
                ))}
                {folderEntries.map(renderEntry)}
                <div className="grid sm:grid-cols-[1fr,auto,auto] gap-2 items-start border-t border-slate-100 pt-2">
                  <input value={dDesc} onChange={e => setDDesc(e.target.value)} placeholder="What the auditee disclosed" className="text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
                  <input type="date" value={dDate} onChange={e => setDDate(e.target.value)} aria-label="Date disclosed" className="text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
                  <button type="button" onClick={addDisclosed} disabled={busy === 'disclosed' || !dDesc.trim()} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50">+ Add</button>
                  <input value={dCorr} onChange={e => setDCorr(e.target.value)} placeholder="Correction status (e.g., corrected, in progress)" className="sm:col-span-2 text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
                  <AttachButton folderKey={folder.key} />
                </div>
              </>
            )}

            {/* Universes — scrub in (results only) + attachments */}
            {folder.universe && (
              <>
                <div className="flex items-center justify-end gap-2 flex-wrap">
                  <AttachButton folderKey={folder.key} />
                  <button type="button" onClick={() => pickScrub(folder.key)} disabled={busy === 'scrub'}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50">
                    {busy === 'scrub' ? 'Scrubbing…' : '↑ Scrub a universe file'}
                  </button>
                </div>
                <div className="text-[11px] text-slate-400 -mt-1">Universe files are scrubbed, never stored — only the results are saved here, named by file so every table stays distinguishable.</div>
                {folderEntries.length === 0 && <div className="text-[12px] text-slate-400">No results saved yet.</div>}
                {folderEntries.map(renderEntry)}
              </>
            )}

            {/* Data integrity — the audit's data check, read-only, plus entries */}
            {folder.key === 'data_integrity' && (
              <>
                {di ? (
                  <div className="border border-slate-100 rounded-lg px-3 py-2 space-y-1">
                    <div className="text-[12px] font-semibold text-slate-700">{di.file_name || 'Universe data check'}</div>
                    <div className="flex flex-wrap gap-1.5 text-[10px]">
                      {audit.data_integrity_status && <span className="rounded-full px-2 py-0.5 font-semibold bg-slate-100 text-slate-600">{String(audit.data_integrity_status).replace('_', ' ')}</span>}
                      {di.metrics?.total_rows != null && <span className="rounded-full px-2 py-0.5 bg-slate-100 text-slate-600">{di.metrics.total_rows} rows · {di.metrics.rows_with_issues ?? 0} with issues</span>}
                    </div>
                    {di.summary && <div className="text-[11px] text-slate-500">{di.summary}</div>}
                  </div>
                ) : <div className="text-[12px] text-slate-400">No data check recorded yet — run one from the Data check panel, or file notes and attachments here.</div>}
                {folderEntries.map(renderEntry)}
              </>
            )}

            {/* Generic folders (areas, notices): entries list */}
            {!folder.universe && folder.key !== 'disclosed_issues' && folder.key !== 'data_integrity' && (
              <>
                {folderEntries.length === 0 && <div className="text-[12px] text-slate-400">Nothing filed here yet.</div>}
                {folderEntries.map(renderEntry)}
              </>
            )}

            {/* Add form + attach — every folder except disclosed (has its own) and universes (scrub owns uploads there) */}
            {!folder.universe && folder.key !== 'disclosed_issues' && (
              <div className="border-t border-slate-100 pt-2 space-y-2">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {['issue', 'note'].map(k => (
                    <button key={k} type="button" onClick={() => setAddKind(k)}
                      className={`text-[11px] px-2.5 py-1 rounded-lg font-semibold ${addKind === k ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-500'}`}>
                      {k === 'issue' ? 'Issue' : 'Note'}
                    </button>
                  ))}
                  <span className="flex-1" />
                  <AttachButton folderKey={folder.key} />
                </div>
                <div className="grid sm:grid-cols-[1fr,auto] gap-2 items-start">
                  <input value={addTitle} onChange={e => setAddTitle(e.target.value)} placeholder={addKind === 'issue' ? 'The issue, in one line' : 'Note title'} className="text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
                  <button type="button" onClick={() => addEntry(folder.key)} disabled={busy === 'add' || !addTitle.trim()} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50">+ Add</button>
                  <textarea value={addDetail} onChange={e => setAddDetail(e.target.value)} rows={2} placeholder="Detail (optional)" className="sm:col-span-2 text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
